import { useIonRouter } from '@ionic/react'
import type { Location } from 'history'
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useHistory } from 'react-router-dom'

/**
 * Per-tab navigation memory for the keep-alive tab shell (TabLayout).
 *
 * The shell has no IonTabs: tab roots live outside the single IonRouterOutlet,
 * so Ionic cannot keep one view stack per tab. This context does the two
 * things IonTabs would do for us, over the plain browser history:
 *
 * - remember the last page of each tab so a tab tap returns to it;
 * - make Back stay inside the tab: it pops the browser history only when the
 *   entry behind belongs to the same tab, otherwise it replaces the page with
 *   its parent route (what Ionic does after a tab switch).
 *
 * To know what sits behind the current entry it mirrors the browser history
 * from `history.listen`. See docs/adr/0008-per-tab-navigation-memory.md.
 */

export type Tab = 'assignments' | 'timeclock' | 'profile'

export const TAB_ROOTS: Record<Tab, string> = {
  assignments: '/assignments',
  timeclock: '/timeclock',
  profile: '/profile',
}

/** Which tab a path belongs to. Anything unknown (e.g. /orders/:id) is
 * shown under Assignments, matching the tab bar highlight. */
export const tabOf = (pathname: string): Tab => {
  if (pathname.startsWith('/timeclock')) return 'timeclock'
  if (pathname.startsWith('/profile')) return 'profile'
  return 'assignments'
}

export type TabNavigation = {
  /** Tab bar tap: restore the tab's last page, or pop the active tab to its root. */
  openTab: (tab: Tab) => void
  /** Back button / edge swipe: pop within the tab, else go to `parentPath`. */
  goBack: (parentPath: string) => void
}

const Context = createContext<TabNavigation | null>(null)

/** `null` outside the tab shell (short-link mini-app). */
export const useTabNavigation = () => useContext(Context)

/** For pages that only ever render inside the tab shell. */
export const useRequiredTabNavigation = (): TabNavigation => {
  const navigation = useContext(Context)
  if (!navigation) {
    throw new Error('useRequiredTabNavigation outside TabNavigationProvider')
  }
  return navigation
}

type Entry = Pick<Location, 'key' | 'pathname' | 'search'>

const toEntry = ({ key, pathname, search }: Location): Entry => ({
  key,
  pathname,
  search,
})

export const TabNavigationProvider = ({ children }: PropsWithChildren) => {
  const history = useHistory()
  // Ionic's own navigate call carries the page transition direction on every
  // history action (the location state is ignored on a REPLACE). `push` is
  // stable, so the context value stays stable too.
  const { push: ionPush } = useIonRouter()
  // Mirror of the browser history: `entries[index]` is the current entry.
  const entries = useRef<Entry[]>([])
  const index = useRef(0)
  const lastPathByTab = useRef<Partial<Record<Tab, string>>>({})

  useEffect(() => {
    const remember = ({ pathname, search }: Entry) => {
      lastPathByTab.current[tabOf(pathname)] = pathname + search
    }
    // Seeded here, not at render: children's mount effects (FallbackRedirect)
    // already ran and may have replaced the location before we subscribe.
    entries.current = [toEntry(history.location)]
    index.current = 0
    remember(entries.current[0])

    return history.listen((location, action) => {
      const entry = toEntry(location)
      if (action === 'PUSH') {
        entries.current = entries.current.slice(0, index.current + 1)
        entries.current.push(entry)
        index.current += 1
      } else if (action === 'REPLACE') {
        entries.current[index.current] = entry
      } else {
        // POP: the browser moved to an entry we already saw (back or forward)
        const known = entries.current.findIndex((e) => e.key === entry.key)
        index.current = known === -1 ? Math.max(index.current - 1, 0) : known
        entries.current[index.current] = entry
      }
      remember(entry)
    })
  }, [history])

  const value = useMemo<TabNavigation>(
    () => ({
      openTab: (tab) => {
        const current =
          entries.current[index.current] ?? toEntry(history.location)
        const root = TAB_ROOTS[tab]
        const alreadyOnTab = tabOf(current.pathname) === tab
        // A tab switch never animates (IonTabs parity): Ionic skips the
        // transition on 'none' and keeps the other tab's views mounted.
        if (!alreadyOnTab) {
          ionPush(lastPathByTab.current[tab] ?? root, 'none')
          return
        }
        // Re-tapping the active tab pops it to its root, animated as a back
        // (IonTabs resets a tab with a 'back' pop)
        if (current.pathname !== root) ionPush(root, 'back')
      },
      goBack: (parentPath) => {
        const current =
          entries.current[index.current] ?? toEntry(history.location)
        const behind = entries.current[index.current - 1]
        const behindIsSameTab =
          behind !== undefined &&
          tabOf(behind.pathname) === tabOf(current.pathname)
        if (behindIsSameTab) {
          history.goBack()
          return
        }
        // Replacing with the parent is still a "back" for the operator
        ionPush(parentPath, 'back', 'replace')
      },
    }),
    [history, ionPush],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}
